import { useState, useRef } from "react";
import { Camera, Loader2, Image as ImageIcon } from "lucide-react";
import { uploadFile, fileUrl, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { AvatarCropModal } from "@/components/AvatarCropModal";

export function ImageUpload({ value, onChange, variant = "avatar", name = "", testId }) {
  const [busy, setBusy] = useState(false);
  const inputRef = useRef();

  const [cropSrc, setCropSrc] = useState(null);
  const pick = () => inputRef.current?.click();

  const closeCrop = () => { if (cropSrc) URL.revokeObjectURL(cropSrc); setCropSrc(null); };

  const upload = async (file) => {
    setBusy(true);
    try {
      onChange(await uploadFile(file));
      toast.success("Photo uploaded");
      closeCrop();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const handleAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image.");
    setCropSrc(URL.createObjectURL(file));
  };

  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const path = await uploadFile(file);
      onChange(path);
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
      e.target.value = "";
    }
  };

  if (variant === "avatar") {
    return (
      <div className="relative inline-block">
        <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-sky-400/40 ring-2 ring-blue-600/20 bg-slate-800 flex items-center justify-center">
          {value ? (
            <img src={fileUrl(value)} alt={name} className="w-full h-full object-cover" />
          ) : (
            <ImageIcon className="text-slate-500" size={28} />
          )}
        </div>
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          data-testid={testId || "avatar-upload-btn"}
          className="absolute -bottom-1 -right-1 w-9 h-9 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center shadow-lg border-2 border-[#070A12] transition-colors"
        >
          {busy ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />}
        </button>
        <input ref={inputRef} type="file" accept="image/*" hidden onChange={handleAvatar} data-testid="avatar-file-input" />
        <AvatarCropModal src={cropSrc} busy={busy} onCancel={closeCrop} onDone={upload} />
      </div>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={pick}
        disabled={busy}
        data-testid={testId || "image-upload-btn"}
        className="iks-input w-full px-3.5 py-3 text-sm flex items-center justify-center gap-2 text-slate-300 hover:border-sky-400/50 transition-colors"
      >
        {busy ? <Loader2 className="animate-spin" size={16} /> : <Camera size={16} />}
        {value ? "Change photo" : "Add photo"}
      </button>
      {value && (
        <img src={fileUrl(value)} alt="" className="mt-3 rounded-xl w-full max-h-52 object-cover" />
      )}
      <input ref={inputRef} type="file" accept="image/*" hidden onChange={handle} />
    </div>
  );
}
